import React, { ReactNode, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface IOSSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  showCloseButton?: boolean;
  showCancelButton?: boolean;
  showAddButton?: boolean;
  onAdd?: () => void;
  [key: string]: any; // 允许传递额外的 props，如 data 属性
}

/**
 * iOS风格的底部浮层组件
 * 参考iOS Calendar.app的设计风格
 */
export const IOSSheet: React.FC<IOSSheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  showCloseButton = false,
  showCancelButton = false,
  showAddButton = false,
  onAdd,
  ...restProps
}) => {
  const { t } = useTranslation(['v3/ios', 'common']);

  // 处理 ESC 键关闭
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // 处理背景点击
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[999] bg-black/30 dark:bg-black/50 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 max-w-md mx-auto max-h-[90vh] bg-ios-gray6 dark:bg-ios-dark-gray6 rounded-t-xl overflow-hidden shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
            {...restProps}
          >
            {/* 顶部栏 */}
            <div className="flex justify-between items-center px-4 pt-3 pb-2 border-b border-ios-gray5 dark:border-ios-dark-gray5 flex-shrink-0">
              {showCancelButton ? (
                <button
                  onClick={onClose}
                  className="text-ios-red dark:text-ios-dark-red text-ios-body font-medium"
                >
                  {t('common:actions.cancel')}
                </button>
              ) : showCloseButton ? (
                <button
                  onClick={onClose}
                  className="text-ios-blue dark:text-ios-dark-blue text-ios-body"
                >
                  {t('v3/ios:sheet.close')}
                </button>
              ) : (
                <div className="w-16"></div>
                /* 占位元素 */
              )}

              {title && (
                <h2 className="text-ios-headline font-semibold text-center">
                  {title}
                </h2>
              )}

              {showAddButton && onAdd ? (
                <button
                  onClick={onAdd}
                  className="text-ios-blue dark:text-ios-dark-blue text-ios-body font-medium"
                >
                  {title?.includes(t('common:actions.create')) ? t('common:actions.add') : t('common:actions.save')}
                </button>
              ) : (
                <div className="w-16"></div> /* 占位元素，保持标题居中 */
              )}
            </div>

            {/* 内容区域 - 自适应高度 */}
            <div className="overflow-y-auto flex-1 pb-safe min-h-0">
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
